/**
 * Slice 2.5 — product card badge: ⭐ 4.6 · 23 · 🔥 143
 *
 * Reads `review_stats` stamped by GET /api/products
 * (`{avg_rating, review_count, fire_count}`). Renders nothing when the
 * product has zero approved reviews — an empty badge on a fresh
 * product is noise, and we don't want customers to see "⭐ 0" on
 * items that just haven't been reviewed yet.
 *
 * Themes:
 *   - `light` (default) — light/bone backgrounds
 *   - `dark`            — for the neon dashboard (dark bg)
 *
 * Variants:
 *   - `compact` (default) — inline row for tight product cards
 *   - `hero`              — larger, used on the featured card
 */
import React from 'react';
import { Star, Flame } from 'lucide-react';

const THEMES = {
  light: {
    text: 'rgba(43, 42, 40, 0.62)',
    mute: 'rgba(43, 42, 40, 0.48)',
    starFill: '#FFD60A',
    fire: '#FF6B00',
  },
  dark: {
    text: '#A3C7A8',
    mute: '#4B6B52',
    starFill: '#FFD60A',
    fire: '#FF6B00',
  },
};

export default function ProductRatingBadge({
  stats, variant = 'compact', theme = 'light', testId,
}) {
  const s = stats || {};
  const count = Number(s.review_count || 0);
  if (count <= 0) return null;
  const avg = Number(s.avg_rating || 0).toFixed(1);
  const fire = Number(s.fire_count || 0);
  const t = THEMES[theme] || THEMES.light;
  const isHero = variant === 'hero';
  const iconSize = isHero ? 14 : 11;
  const fontFamily = "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace";

  const rootStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: isHero ? 8 : 6,
    fontFamily,
    fontSize: isHero ? 13 : 11,
    color: t.text,
    lineHeight: 1,
  };
  const chunk = { display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 600, fontVariantNumeric: 'tabular-nums' };
  const sep = { opacity: 0.55, fontWeight: 500 };
  const muted = { color: t.mute };

  return (
    <span style={rootStyle} data-testid={testId || 'product-rating-badge'}>
      <span style={chunk}>
        <Star size={iconSize} fill={t.starFill} color={t.starFill} strokeWidth={0} />
        <span>{avg}</span>
      </span>
      <span style={sep}>·</span>
      <span style={muted}>{isHero ? `${count} review${count === 1 ? '' : 's'}` : count}</span>
      {fire > 0 && (
        <>
          <span style={sep}>·</span>
          <span style={{ ...chunk, color: t.fire }}>
            <Flame size={iconSize} color={t.fire} fill={t.fire} strokeWidth={0} />
            <span>{fire}</span>
          </span>
        </>
      )}
    </span>
  );
}
